import React, {Fragment, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, HStack, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import Swatch from '../swatch-group/swatch'
import SwatchGroup from '../swatch-group'
import ProductThumbnail from '../product-thumbnail'
import {FormattedMessage} from 'react-intl'
import {selectablesValuesFiltered} from '../../custom-hooks/use-custom-variation-group'
import {remCalc} from '../../theme/_utils'
import {
    PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID,
    PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID,
} from '../../constants'
import {BRAND_KEY} from '../../brand'
import ProductVariantsSelector from './product-variants-selector'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {isServer} from '../../utils/utils'

function ProductVariants({
    variationAttributes,
    product,
    lastItemThreshold,
    InfoStockIcon,
    OutOfStockIcon,
    LastFewItemsVariant,
    OutOfStockVariant,
    priceData,
    activeCurrency,
}) {
    const styles = useStyleConfig('ProductVariants', {})
    const {isOpen, onOpen, onClose} = useDisclosure()

    const ids = product?.c_enabledVariationGroups?.map(({productId}) => productId)
    const {data: variantsInSwatch} = useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                expand: ['availability'],
                select: '(data.(id,c_variants,type))',
            },
            headers: {
                'x-debug': 'product-view-index',
            },
        },
        {
            enabled: !isServer && ids?.length > 0,
            keepPreviousData: true,
        },
    )

    const mapping = useMemo(() => {
        return (
            variantsInSwatch?.data?.reduce((acc, item) => {
                if (item.c_variants && item.c_variants.length > 0) {
                    acc[item.id] = item.c_variants[0].productId
                }
                return acc
            }, {}) || {}
        )
    }, [variantsInSwatch?.data])

    return variationAttributes.map(({id, name, selectedValue, values = []}) => {
        if (id === PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID) return

        if (id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID)
            values = selectablesValuesFiltered(values, product?.c_enabledVariationGroups ?? [])

        if (BRAND_KEY === 'braun') {
            return (
                <ProductVariantsSelector
                    key={id}
                    styles={styles}
                    isOpen={isOpen}
                    onClose={onClose}
                    product={product}
                    activeCurrency={activeCurrency}
                    lastItemThreshold={lastItemThreshold}
                    priceData={priceData}
                    LastFewItemsVariant={LastFewItemsVariant}
                    OutOfStockVariant={OutOfStockVariant}
                    InfoStockIcon={InfoStockIcon}
                    OutOfStockIcon={OutOfStockIcon}
                    onOpen={onOpen}
                />
            )
        }

        const swatches = values.map((props, index) => {
            let {href, name, image, value, orderable} = props

            const variation = product?.c_enabledVariationGroups?.find(
                (item) => item.productId === value,
            )

            if (variation) {
                href = variation?.slugUrl
                const pid = mapping?.[variation.productId]
                if (pid) {
                    href += '?pid=' + pid
                }
            }

            const hasSelection = Boolean(selectedValue?.value)
            const isSelected = selectedValue?.value === value
            let content

            if (image) {
                content = (
                    <ProductThumbnail
                        handleSelection={() => {}}
                        selected={isSelected}
                        image={image}
                        variant="colorThumbnail"
                    />
                )
            } else {
                content = name
            }
            const isFirst = index === 0
            // To mimic the behavior of a native radio input, only
            // one swatch should receive tab focus; the rest can be
            // selected using arrow keys when the swatch group has
            // focus. The focused element is the selected option or
            // the first in the group, if no option is selected.
            // This is a slight difference, for simplicity, from the
            // native element, where the first element is focused on
            // `Tab` and the _last_ element is focused on `Shift+Tab`
            const isFocusable = isSelected || (!hasSelection && isFirst)

            return (
                <Swatch
                    key={value}
                    href={href}
                    disabled={!orderable}
                    value={value}
                    name={name}
                    variant={id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID ? 'image' : 'square'}
                    selected={isSelected}
                    isFocusable={isFocusable}
                    position={index + 1}
                    total={values?.length}
                >
                    {content}
                </Swatch>
            )
        })

        if (id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID && swatches.length === 1) {
            if (selectedValue?.name?.toLowerCase() === 'undefined')
                return <Fragment key={id}></Fragment>

            return (
                <HStack
                    key={id}
                    color="text.default"
                    fontSize={remCalc(14)}
                    data-ab="variant-colors"
                >
                    <Box fontWeight="regular">
                        {id === PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID ? (
                            <FormattedMessage id="variants.label.color" defaultMessage="Color" />
                        ) : (
                            name
                        )}
                        :
                    </Box>
                    <Box fontWeight="bold">{selectedValue?.displayValue || ''}</Box>
                </HStack>
            )
        }

        return (
            <SwatchGroup
                key={id}
                value={selectedValue?.value}
                displayName={selectedValue?.displayValue || ''}
                id={id}
                label={name}
            >
                {swatches}
            </SwatchGroup>
        )
    })
}

ProductVariants.propTypes = {
    variationAttributes: PropTypes.array,
    product: PropTypes.object,
    lastItemThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    InfoStockIcon: PropTypes.elementType,
    OutOfStockIcon: PropTypes.elementType,
    LastFewItemsVariant: PropTypes.string,
    OutOfStockVariant: PropTypes.string,
    priceData: PropTypes.object,
    activeCurrency: PropTypes.string,
}

export default ProductVariants
